/* eslint-disable no-empty */
import { useState, useRef } from "react";

import "./index.less";
import { uniqueId } from "@/utils";
import ChatBlock from "./components/ChatBlock";
import QuestionInput from "./components/QuestionInput";
import HeaderBanner from "./components/HeaderBanner";
import ActionTools from "./components/ActionTools";
import KnowledgeTool from "./components/KnowledgeTool";
import ClearHistoryModal from "./components/ClearHistoryModal";
import KnowledgeAnswer from "./components/ChatBlock/KnowledgeAnswer";
import chatPageHook from "./chatPageHook";
import useStreamFetch from "./useSSEFetch";

const CssChatContent = "KaiHong-FAQ";
const localStorageKey = "FAQ-Chat-List";

export default function ITAssistant() {
  const [value, setValue] = useState("");
  const [chatList, setChatList] = useState<KnowledgeRetrievalOutput[]>([]);
  const [isThinking, setIsThinking] = useState(false); // 是否正在思考中
  const { isStreaming, abortRequest, startStreamRequest } = useStreamFetch();
  
  const { chatBox, scrollToBottom, setChatHistory } = chatPageHook({ 
    setChatList: setChatList, 
    storageKey: localStorageKey, 
    abort: abortRequest 
  });
  const lastQuestion = useRef<string>("");

  const sendQuestion = async (value: string) => {
    try {
      if (!value) return;
      console.log('cur sendQuestion init render => ', isThinking)
      setChatList((prevList) => [
        ...prevList,
        {
          role: "user",
          id: uniqueId(),
          outputs: {
            summary: value,
            result: [],
          },
          status: "succeeded",
          question: value,
        },
      ]);
      lastQuestion.current = value;
      setValue("");
      const assistantId = uniqueId(); // 第一次生成 ID
      Promise.resolve().then(() => {
        setIsThinking(true);
        setChatList((prevList) => {
          return [
            ...prevList,
            {
              role: "assistant",
              id: assistantId,
              outputs: {
                summary: "思考中",
                result: [],
              },
              status: "loading",
              question: lastQuestion.current,
            },
          ];
        });
        scrollToBottom();
      });
  
      console.log('cur isThinking render => ', isThinking)

      let chunk = '';
      startStreamRequest(value, (newChunk: any) => {
        chunk += newChunk;
        setIsThinking(false);
        setChatList((prevList) => {
          const lastOne = prevList.pop();
          const newList: any = [
            ...prevList,
            {
              ...lastOne,
              outputs: {
                summary: chunk,
                result: [],
              },
              status: 'succeeded',
              id: assistantId,
            }
          ];
          // setChatHistory(localStorageKey, newList);
          return newList;
        });
        scrollToBottom();
      });
    } catch (e) {
      console.error("sendQuestion", (e as any)?.message);
      errorTextMsg(e);
    } finally {
      setIsThinking(false);
    }
  };
  

  const errorTextMsg = (e: any) => {
    const errMsg = e?.message === "abort" ? "本次回答已被终止" : "网络联接失败，无法处理该请求。";

    // 如果是中止导致的报错
    setChatList((prevList) => {
      const lastOne = prevList.pop();
      const newList: KnowledgeRetrievalOutput[] = [
        ...prevList,
        {
          ...lastOne!,
          status: e.message === "abort" ? "aborted" : "failed",
          outputs: {
            summary: errMsg,
            result: [],
          },
        },
      ];
      setChatHistory(localStorageKey, newList);
      return newList;
    });
    scrollToBottom();
  };

  const onResend = (data: KnowledgeRetrievalOutput) => {
    if (isThinking) {
      window.message.info("当前问题还在思考中，请等待回答完毕后再重试");
      return;
    }
    sendQuestion(data.question || "");
  };

  // 中止当前的请求
  const abortAnswer = () => {
    setIsThinking(false);
    abortRequest();

     // 更新最后一个 Assistant 回答状态
      setChatList((prevList) => {
        const lastOne = prevList[prevList.length - 1];
        if (!lastOne || lastOne.role !== 'assistant' || lastOne.status !== 'loading') return prevList;

        const updatedList = [...prevList];
        updatedList[updatedList.length - 1] = {
          ...lastOne,
          status: 'aborted',
          outputs: {
            summary: '本次回答已被中止',
            result: [],
          },
        };
        setChatHistory(localStorageKey, updatedList);
        return updatedList;
      });

      scrollToBottom(); // 保证更新后滚动到底部
    
  };
  // 清空当前页面的记录
  const [clearOpen, setClearOpen] = useState(false);

  const onOpenCLearModal = () => {
    if (isThinking) return;
    setClearOpen(true);
  };
  const onClearHistory = () => {
    setChatList([]);
    setClearOpen(false);
    setChatHistory(localStorageKey, []);
  };

  const openReferenceDrawer = (data: KnowledgeRetrievalOutput) => {
    console.log("onExpandSource :>> ");
  };

  return (
    <div className={CssChatContent}>
      <div className={`${CssChatContent}-left`}>
        <div className={`${CssChatContent}-left_chat no-scrollbar`} ref={chatBox}>
          <HeaderBanner secondLine="欢迎使用深开鸿 AI 助手！">
            <div className="header-banner__sub">
              作为你的私人助手，我擅长剖析复杂问题，挖掘深层逻辑，并为你提供精准的洞察与多维度的解决方案
            </div>
          </HeaderBanner>
          {chatList.map((chatItem) => {
            return (
              <ChatBlock
                data={chatItem}
                key={chatItem.id}
                onResend={onResend}
                actions={["copy", "resend"]}
                isStreaming={isThinking || isStreaming}
                onEditQuestion={(ques) => {
                  setValue(ques);
                }}
              >
                <KnowledgeAnswer data={chatItem} onExpandSource={() => openReferenceDrawer(chatItem)} />
              </ChatBlock>
            );
          })}
        </div>
        <ActionTools showAbortButton={isThinking || isStreaming} onAbort={abortAnswer}>
          {!isThinking && !isStreaming && <KnowledgeTool onClearHistory={onOpenCLearModal} />} 
        </ActionTools>
        <div className={`${CssChatContent}-left_bottom`}>
          <QuestionInput
            inputValue={value}
            setInputValue={setValue}
            onSend={sendQuestion}
            isLoading={isThinking || isStreaming}
            maxLength={999}
            defaultDisplayContent="请输入您的问题"
          />
        </div>
      </div>
      <div className="chat-content-right"></div>
      <ClearHistoryModal open={clearOpen} onCancel={() => setClearOpen(false)} onOk={onClearHistory} />
    </div>
  );
}
